import { FilterXSS, getDefaultWhiteList, safeAttrValue } from 'xss';

import { isYoutubeEmbedUrl } from './youtube';

// TipTap emits Tailwind classes (list styling, spacing) and inline text-align
// styles — allow them on every whitelisted tag so sanitized output keeps its look.
const defaultWhiteList = Object.fromEntries(
	Object.entries(getDefaultWhiteList()).map(([tag, attrs]) => [
		tag,
		// `rel` matters on links: TipTap writes noopener/noreferrer/nofollow alongside
		// target="_blank", and dropping it leaves the opened page able to rewrite our tab.
		[...(attrs ?? []), 'class', 'style', ...(tag === 'a' ? ['rel'] : [])],
	])
);

// `xss` has no `iframe` entry and gives `div` no attributes, so the embed and the
// `data-youtube-video` wrapper the editor re-parses would both be stripped.
const richTextWhiteList = {
	...defaultWhiteList,
	div: [...(defaultWhiteList.div ?? []), 'data-youtube-video'],
	iframe: ['src', 'width', 'height', 'allow', 'allowfullscreen', 'title', 'class', 'style'],
};

// Pure-JS sanitizer (no jsdom): safe to run in Node, edge and browser, and
// deterministic across all of them — jsdom broke on Vercel (ERR_REQUIRE_ESM)
// and could differ from browser output.
const richTextSanitizer = new FilterXSS({
	whiteList: richTextWhiteList,
	stripIgnoreTag: true,
	stripIgnoreTagBody: ['script', 'style'],
	// A whitelist entry alone allows any `src`, so non-YouTube iframes fail closed to an empty frame.
	safeAttrValue: (tag, name, value, cssFilter) => {
		if (tag === 'iframe' && name === 'src') return isYoutubeEmbedUrl(value) ? value : '';
		return safeAttrValue(tag, name, value, cssFilter);
	},
});

/**
 * Sanitizes creator-authored rich text (TipTap HTML) before rendering it
 * outside the TipTap editor, which otherwise schema-filters unsafe nodes.
 */
export function sanitizeRichTextHtml(html: string): string {
	if (!html) return '';
	return richTextSanitizer.process(html);
}

const TRAILING_BREAK_REGEX =
	/<br\s*\/?>\s*(<\/(?:p|li|h[1-6]|div|blockquote|td|th|figcaption)>)/gi;

// Browsers drop a <br> that ends a block, so a trailing Shift+Enter vanishes
// outside TipTap — anchor it with an nbsp, same trick as `.tiptap p:empty`.
export function preserveTrailingLineBreaks(html: string): string {
	if (!html) return '';
	return html.replace(TRAILING_BREAK_REGEX, '<br>&#160;$1');
}
